import * as _ from 'lodash';
import { Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BidService } from '../../services/bid.service';
import { OrganizationService } from '../../services/org.service';
import { debounceTime, distinct } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subject, Subscription } from 'rxjs';
import { ToastService } from '../../../toast/services/toast.service';
import { Store } from '@ngrx/store';
import { UtilsService } from '../../services/utils.service';
import * as DialogActions from '../../../dialog/features/dialog.actions';
import { IBidRequestParams, IRecentActivity, ItemsType } from '../../../campaign/models/activity.model';
import { IOptionInfo } from '../../../event/models/event.model';
import { LocalStorageService } from '../../services/local-storage.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AuctionHelperService, IAuctionStatusDetails } from '../../../core/services/auction-helper.service';
import { AMOUNT_LIMIT } from 'app/virtual-event/virtual-event.enums';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-all-bids',
  templateUrl: './all-bids.component.html',
  styleUrls: ['./all-bids.component.scss']
})
export class AllBidsComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  @ViewChild('desktopBidTemplate', { static: true }) public desktopBidRef: ElementRef;
  @ViewChild('tabletBidTemplate', { static: true }) public tabletBidRef: ElementRef;
  @ViewChild('mobileBidTemplate', { static: true }) public mobileBidRef: ElementRef;

  @Input() id = '';
  @Input() org = '';
  @Input() ppid = 0;

  public AMOUNT_LIMIT = AMOUNT_LIMIT;
  
  // User Data
  public email;
  public name;
  public phone;
  public userInfo;
  public types = ItemsType;

  public campaign: any = null;
  public payeeInfo: any = null;
  public quickPledge: any = null;

  public isShowActivities:any = true;

  public auctionControlMethod = 0;
  public list: any[] = [];
  public winningBids: any[] = [];
  public ctx = {};
  public selectedBidId = null;
  public selectedBidType = false;
  public helpText = '';
  public minBidAmount = 0;
  public recentActivities: IRecentActivity[] = [];
  public featuredOptions: IOptionInfo[] = [];
  public highlightActivities: IRecentActivity[] = [];
  public timestamp = 0;
  public nextAttempt = 0;
  public rankedCount = 0;
  public auctionStatusDetails: IAuctionStatusDetails;
  public manualStatus;

  public counterSegments = [{
    label: 'd',
    duration: 60 * 60 * 24,
    required: false
  }, {
    label: 'h',
    duration: 60 * 60,
    required: false
  }, {
    label: 'm',
    duration: 60,
    required: true
  }, {
    label: 's',
    duration: 1,
    required: true
  }]

  public form: UntypedFormGroup;

  public displayNameMap = new Map([
    ['(max-width: 767px)', 'Small'],
    ['(min-width: 768px) and (max-width: 1199px)', 'Medium'],
    ['(min-width: 1200px)', 'Large'],
  ]);
  public screenSize: string = null;

  public subscription = new Subscription();
  public currentAmount = 0
  public calculatedNextBidAmount = 0
  public currentAmountControl: UntypedFormControl = new UntypedFormControl(0, []);
  private subject: Subject<any> = new Subject();

  constructor(
    public auctionHelperService: AuctionHelperService,
    public breakpointObserver: BreakpointObserver,
    public bidService: BidService,
    public organizationService: OrganizationService,
    private toastService: ToastService,
    private store: Store,
    private utilsService: UtilsService,
    private localStorageService: LocalStorageService,
  ) {
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([
      '(max-width: 767px)',
      '(min-width: 768px) and (max-width: 1199px)',
      '(min-width: 1200px)',
    ]).pipe(
      debounceTime(100),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      for (const query of Object.keys(result.breakpoints)) {
        if (result.breakpoints[query]) {
          this.screenSize = this.displayNameMap.get(query);
        }
      }
    });

    this.subscription.add(
      this.bidService.getBidList(this.id, this.org, this.ppid)
        .subscribe(({ campaignData, bids, payee }: any) => {
          this.updateScreenData(campaignData, bids, payee, true);
          this.bidService.getBidActivities({
            campaignId: this.campaign.id,
            since: this.timestamp,
            count: 10,
          }).subscribe((recentActivities: IRecentActivity[]) => {
            this.updateRecentActivities(recentActivities, true);
          });

          this.bidService.getFeaturedList(this.campaign.id).subscribe((featuredOptions: any[]) => {
            this.featuredOptions = featuredOptions;
          });
        })
    );

    this.subject.pipe(
      distinct(),
      debounceTime(5000),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(timestamp => {
      this.bidService.getBidActivities({
        since: this.timestamp,
        campaignId: this.campaign.id,
        count: 0,
      }).subscribe((recentActivities: IRecentActivity[]) => {
        this.updateRecentActivities(recentActivities);
      });
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
  }

  getBidList(isFirstCall = false) {

    this.bidService.getBidList(this.id, this.org, this.ppid)
      .subscribe(({ campaignData, bids, payee }: any) => {
        this.updateScreenData(campaignData, bids, payee, isFirstCall );
        this.bidService.getBidActivities({
          campaignId: this.campaign.id,
          since: this.timestamp,
          count: isFirstCall ? 10 : 0,
        }).subscribe((recentActivities: IRecentActivity[]) => {
          this.updateRecentActivities(recentActivities, isFirstCall);
        });
      });
  }

  updateScreenData(campaignData, bids, payee,  firstCall = false ): void {
    this.payeeInfo = payee
    this.campaign = campaignData;
    this.campaign.status = this.auctionHelperService.getAuctionStatus(this.campaign.campaignTypeData);
    this.isShowActivities = this.campaign.campaignTypeData.showBidHistory;

    this.subscription.add(this.auctionHelperService.getAuctionsStatusDetails(this.campaign).subscribe(auctionStatusDetails => {
      this.auctionStatusDetails = auctionStatusDetails;
      this.auctionControlMethod = this.auctionStatusDetails.auctionControlMethod;

      this.manualStatus = this.auctionHelperService.getManualAuctionStatus(campaignData, auctionStatusDetails);
    }));

    this.updateQuickPledgeGlobal(payee, campaignData);
    this.auctionControlMethod = _.get(this.campaign, 'campaignTypeData.auctionControlMethod', 0);
    this.list = bids;

    if (this.list && this.list.length) {
      this.winningBids = this.list.filter(bid => bid.winningBid === true)
      this.rankedCount = this.list.filter(bid => bid.currentRank === 0).length;
      this.userInfo = this.list[0].userInfo;
      this.localStorageService.setUser(this.userInfo);

      if (this.list[0].additionalInfoSelect) {
        this.localStorageService.setItem('additionalInfoBidSelect_' + this.campaign.id, this.list[0].additionalInfoSelect);
      }

      if (this.list[0].additionalInfo) {
        this.localStorageService.setItem('additionalInfo_' + this.campaign.id, this.list[0].additionalInfo);
      }
    }
  }

  updateRecentActivities(recentActivities: IRecentActivity[], isFirstCall = false): void {
    this.highlightActivities = [];

    if (isFirstCall || recentActivities?.length) {
      this.highlightActivities = !isFirstCall ? recentActivities : [];

      this.timestamp = Math.round(Date.now() / 1000);
      this.recentActivities = [].concat(recentActivities, this.recentActivities);

      this.recentActivities = _.uniqBy(this.recentActivities, 'id').slice(0, 10);
    }

    if (!isFirstCall && recentActivities?.length) {
      recentActivities.forEach(activity => {
        const indexInList = this.list.findIndex(listItem => listItem.presetId === activity.presetId);

        if (indexInList >= 0) {
          const listActivity = this.list[indexInList];
          listActivity.currentItemBid = activity.amount;
          listActivity.optionInfo.currentBid = activity.amount;
          listActivity.currentRank = listActivity.userId !== activity.userId ? 1 : 0;

          this.list.splice(indexInList, 1);
          this.list.unshift(listActivity);
        }
      });
    }

    this.nextAttempt += 1;
    this.subject.next(this.nextAttempt);
  }

  isHighLight(id): IRecentActivity {
    return this.highlightActivities.find(activity => {
      return activity.id === id;
    });
  }


  backToAuctionItems() {
    this.quickPledge.payeeInfo.selectedPreset = null;
    this.quickPledge.selectedPreset = null;

    this.quickPledge.loadOptions(true);
  }

  getTemplateOutlet() {
    switch (this.screenSize) {
      case 'Large':
        return this.desktopBidRef;
      case 'Medium':
        return this.tabletBidRef;
      case 'Small':
        return this.mobileBidRef;
    }
    return this.desktopBidRef;
  }

  getTemplateOutletContex(bid, idx) {
    return { bid, idx };
  }

  getImageUrl(bid) {
    const imageUrl = _.get(bid.optionInfo, 'imageUrl', '');
    return _.isEmpty(imageUrl) ? '/images/org_logo_default_v2.svg' : imageUrl;
  }

  getCampaignUrl() {
    if (this.campaign && this.campaign.imageUrl !== undefined
      && this.campaign.imageUrl !== '') {
      return this.campaign.imageUrl;
    } else {
      if (this.payeeInfo) {
        return this.payeeInfo && this.payeeInfo.image || '/images/org_logo_default_v2.svg';
      }
      return '/images/org_logo_default_v2.svg';
    }
  }

  getRankClass(bid) {
    return {
      'rank--first': bid.currentRank < 1,
      'rank--not-first': bid.currentRank > 0
    };
  }

  getCampaignStatus() {
    switch (this.campaign?.status) {
      case 0:
        return 'Active';
      case 1:
        return 'Expired';
      case 2:
        return 'Deactivated';
      case 3:
        return 'Not Started';
      case 5:
        return 'Archived';
    }

  }

  openBidPopup(bid) {
    this.selectedBidId = bid.id;
    // must be removed after BE fixes
    bid.optionInfo.currentBid = bid.amount;
    this.currentAmountControl.markAsUntouched();
    // reset bid type if open modal
    this.selectedBidType = false;

    this.setNextBidAmount(bid);
    this.getMinBidAmount(bid);
  }

  getMinBidAmount(bid) {
    this.minBidAmount = bid.optionInfo.minBid;

    if (bid.currentItemBid > 0.0) {
      this.minBidAmount = bid.currentItemBid + bid.optionInfo.bidIncrement;
    }

    if (this.selectedBidType) {
      this.minBidAmount = this.minBidAmount + bid.optionInfo.bidIncrement;
    }

    return this.minBidAmount;
  }

  onShowConfirmModal(bid): void {
      const title = this.selectedBidType ? 'Confirm Max Bid Amount?' : 'Confirm Bid Amount?';
      const text = this.selectedBidType ?
        `Are you sure you want to set up auto bidding up to <span style="font-weight: 500">${this.utilsService.formatCurrency(this.currentAmountControl.value)}</span> for this item?`:
        `Are you sure you want to bid <span style="font-weight: 500">${this.utilsService.formatCurrency(this.currentAmountControl.value)}</span> on this item ?`;

      const action = DialogActions.openDialog({
        payload: {
          name: 'bidConfirm',
          options: {
            size: 'small',
            modalClass: 'modal--paybee modal-sm',
            containerClass: 'mdb-modal-backdrop-paybee'
          },
          initialState: {
            modalTitle: title,
            modalText: text,
            callback: (result) => {
              if (result) {
                this.confirmBid(bid);
              }
            }
          }
        }
      });

      this.store.dispatch(action);
  }

  onClickConfirm(bid) {
    const utils = window['utils'];
    const quickPledge = window['quickPledge'];

    let isBidAmountExceed = false;
    this.minBidAmount = bid.optionInfo.minBid;
    this.currentAmount = this.currentAmountControl.value

    if (bid.currentItemBid > 0.0) {
      this.minBidAmount = bid.currentItemBid + bid.optionInfo.bidIncrement;
      isBidAmountExceed = this.currentAmount > (bid.currentItemBid + 10 * bid.optionInfo.bidIncrement);
    } else {
      isBidAmountExceed = this.currentAmount >  (this.minBidAmount + 10 * bid.optionInfo.bidIncrement);
    }

    if (this.selectedBidType) {
      this.minBidAmount = this.minBidAmount + bid.optionInfo.bidIncrement;
    }

    const showBidAmountExceedDialog = () => {
      return new Promise((resolve, reject) => {
          const modalTitle = this.selectedBidType ? 'Confirm Max Bid Amount?' : 'Confirm Bid Amount?';
          const modalMessage = this.selectedBidType ?
              `Are you sure you want to set up auto bidding up to ${ utils.getAmountDisplay(this.currentAmount)  } for this item?` :
              `Are you sure you want to bid ${ utils.getAmountDisplay(this.currentAmount) } on this item ?`;
  
          return quickPledge.confirmDialogSimple({
              title: modalTitle,
              message: modalMessage,
              yes: () => {
                  return resolve({
                    action: 'showBidAmountExceedDialog',
                    value: true
                  });
              },
              no: () => {
                  return reject({
                    action: 'showBidAmountExceedDialog',
                    value: false
                  });
              },
              onHidden: () => {
                  return reject({
                    action: 'showBidAmountExceedDialog',
                    value: false
                  });
              }
          });
      });
    }

    const showBidAmountHighestDialog = () => {
      return new Promise((resolve, reject) => {
          quickPledge.confirmDialogSimple({
              title: "Are you sure you want to bid again?",
              message: "You are currently the highest bidder for this item.",
              yes: () => {
                  return resolve({
                    action: 'showBidAmountHighestDialog',
                    value: true
                  });
              },
              no: () => {
                  return reject({
                    action: 'showBidAmountHighestDialog',
                    value: false
                  });
              },
              onHidden: () => {
                  return reject({
                    action: 'showBidAmountHighestDialog',
                    value: false
                  });
              }
          })
      });
    }

    let promise: any = Promise.resolve();

    if (isBidAmountExceed) {
        promise = promise.then(() => {
            return showBidAmountExceedDialog()
        })
    }

    if (this.userInfo.email === bid.userInfo.email) {
        promise = promise.then(() => {
            return showBidAmountHighestDialog()
        });
    }
    const campaignTypeData = bid.campaignData.campaignTypeData;

    const makeBidFlow = (promise) => {
      let userAccessToken = quickPledge.getUserAccessToken();
      if(campaignTypeData.verifyEmailForBidding) {
          if(userAccessToken === null) {
              promise = promise.then(() => {
                  return quickPledge.verifyEmailModal(this.userInfo.name, this.userInfo.email, this.campaign);
              });
          } else if(campaignTypeData.saveCardRequired) {
              promise = promise.then(() => {
                  return quickPledge.verifyEmailModal(this.userInfo.name, this.userInfo.email, this.campaign, 3);
              })
          }
      }
  
      if(campaignTypeData.saveCardRequired) {
        promise = promise.then((response) => {
            const modal: any = $("#verifyEmailModal");
            modal.modal("hide");
  
            const userName = this.userInfo.name;
            const userEmail = this.userInfo.email;
            const userPhone = this.userInfo.phone;
        
            const additionalInfo = quickPledge.getAdditionalInfoBid(this.campaign.id) || ''
            const additionalInfoSelect = quickPledge.getAdditionalInfoBidSelect(this.campaign.id)
        
            const params: IBidRequestParams = {
                campaignId: bid.campaignData.id,
                presetId: bid.optionInfo.id,
                bidAmount: this.currentAmount,
                client: 'web',
                email: userEmail,
                name: userName,
                phone: userPhone,
                additionalInfo: additionalInfo || '',
                additionalInfoSelect: additionalInfoSelect || '',
            };
  
            quickPledge.selectedPreset = bid.optionInfo;
  
            quickPledge.oneTimePaymentParams = {
                type: 10,
                userAccessToken: quickPledge.getUserAccessToken(),
                hideCoverFee: bid.campaignData.hideCoverFee,
                isAutoBid: this.selectedBidType,
                isDashboardView: true,
                params
            }
            
            if(response.result === null) {
                $('#rightContainerCampaignInfo').show();
                $("#rightBarCampaignInfo").show();
                
                quickPledge.showOneTimePayment(true);
                return Promise.reject({});
            } else if(typeof response.result?.fundingSourceId === 'string') {
                return quickPledge.doAuctionSaveCard({
                    type: 'stored',
                    methodId: response.result.fundingSourceId,
                    feesPaidByDonor: false
                }).then(() => {
                    return Promise.reject({});
                });
            }
  
            return response;
        });
      }
  
      promise.then(() => {
        const modal: any = $("#verifyEmailModal");
        modal.modal("hide");
        return this.confirmBid(bid);
      }).catch((error) => {
        switch(error.errorCode) {
          case "Internet Down":
              campaignTypeData.verifyEmailForBidding = true;
              return makeBidFlow(Promise.resolve());
          case "AuctionCardNotSaved":
              campaignTypeData.verifyEmailForBidding = true;
              campaignTypeData.saveCardRequired = true;
              return makeBidFlow(Promise.resolve());
        }
  
        const modal: any = $("#verifyEmailModal");
        modal.modal("hide");
      });

      return promise;
    }

    return makeBidFlow(promise);
  }

  updateQuickPledgeGlobal(payeeInfo, campaign) {
    this.quickPledge = window['quickPledge'];

    this.quickPledge.payeeInfo = payeeInfo;
    this.quickPledge.payeeInfo.selectedCampaign = campaign;
    this.quickPledge.selectedCampaign = campaign;

    this.quickPledge.setBannerColors(this.payeeInfo.bannerBkColor, this.payeeInfo.bannerTextColor);
    this.quickPledge.setTopNav(false);
  }

  getQuickPledge() {
    return this.quickPledge;
  }

  confirmBid(bid) {
    this.selectedBidId = bid.id;

    this.quickPledge.selectedPreset = bid.optionInfo;
    this.quickPledge.payeeInfo.selectedPreset = bid.optionInfo;

    const userName = this.userInfo.name;
    const userEmail = this.userInfo.email;
    const userPhone = this.userInfo.phone;

    const additionalInfo = this.quickPledge.getAdditionalInfoBid(this.campaign.id) || ''
    const additionalInfoSelect = this.quickPledge.getAdditionalInfoBidSelect(this.campaign.id)

    const params: IBidRequestParams = {
      campaignId: bid.campaignData.id,
      presetId: bid.optionInfo.id,
      bidAmount: this.currentAmount,
      client: 'web',
      email: userEmail,
      name: userName,
      phone: userPhone,
      additionalInfo: additionalInfo || '',
      additionalInfoSelect: additionalInfoSelect || '',
    };

    if (this.quickPledge.selectedCampaign.streetAddressRequired) {
      const streetAddress = this.quickPledge.getStreetAddress();
      if (streetAddress.street1) {
        params.streetAddress = streetAddress;
      }
    }

    this.selectedBidId = null;

    if (!this.selectedBidType) {
      this.bidService.addNewBid(params).subscribe(() => {
        const message = `A bid of ${this.utilsService.formatCurrency(this.currentAmount)} Accepted`;
        this.toastService.show('success', message);

        this.getBidList();
        this.currentAmountControl.setValue(0)
        this.currentAmount = 0;

        }, () => {
          this.getBidList();
      });
    }

    if (this.selectedBidType) {
      this.bidService.addNewAutoBid(params).subscribe(() => {
        const message = `Your auto bid has been successfully registered for the ${bid.optionInfo.name}`;
        this.toastService.show('success', message);

        this.getBidList();
        this.currentAmountControl.setValue(0)
        this.currentAmount = 0;

      }, () => {
        this.getBidList();
      });
    }
  }

  buttonClass(bid) {
    const selectedPresetOption = bid.optionInfo;
    const soldOut = selectedPresetOption.soldCount >= selectedPresetOption.count;
    return {
      'modal-sold-out-button': soldOut,
    };
  }

  buttonDisabled(bid) {
    const selectedCampaign = this.campaign || bid.campaignData;
    const selectedPresetOption = bid.optionInfo;

    const { status } = this.quickPledge.getAuctionStatus(selectedCampaign.campaignTypeData);
    if (status !== 0) {
      return true;
    }

    if (selectedPresetOption.buyItNowOnlyItem) {
      return true;
    }

    return selectedPresetOption.soldCount >= selectedPresetOption.count;
  }

  swtcherDisabled(bid){
    const selectedCampaign = this.campaign || bid.campaignData;
    return selectedCampaign.campaignTypeData.autoBidDisabled
  }

  confirmButtonDisabled(bid) {
    const selectedCampaign = bid.campaignData;
    const selectedPresetOption = bid.optionInfo;

    const { status } = this.quickPledge.getAuctionStatus(selectedCampaign.campaignTypeData);

    if (status === 1) {
      return true;
    }

    if (selectedPresetOption.buyItNowOnlyItem) {
      return true;
    }

    this.currentAmount = this.currentAmountControl.value;

    if (this.minBidAmount > this.currentAmount) {
      return true;
    }

    if (this.currentAmount > AMOUNT_LIMIT.GLOBAL_MAX) {
      return true;
    }

    return selectedPresetOption.soldCount >= selectedPresetOption.count;
  }

  getButtonText(bid) {
    const selectedPresetOption = bid.optionInfo;
    const soldOut = selectedPresetOption.soldCount >= selectedPresetOption.count;
    return soldOut ? 'Sold Out' : 'Bid Again';
  }

  onBuyNowHandler(bid) {
    this.quickPledge.selectedPreset = bid.optionInfo;
    this.quickPledge.payeeInfo.selectedPreset = bid.optionInfo;
    this.quickPledge.showBuyItNow({ id : 'id_' + this.campaign.id + '_' + bid.optionInfo.id } , {
      campaignId: this.campaign.id,
      optionInfo: bid.optionInfo,
    }, true );
  }

  changeBidType(type, bid) {
    this.selectedBidType = type;

    this.setNextBidAmount(bid);
    this.getMinBidAmount(bid);

    // must be removed after BE fixes
    bid.optionInfo.currentBid = bid.amount;
  }

  setNextBidAmount(bid) {
    if (!this.currentAmountControl.touched) {
      this.currentAmount = 0;
      this.currentAmountControl.setValue(0);
    }

    if (this.selectedBidType) {
      this.currentAmount = this.getNextBidAmount(bid, true);
      this.currentAmountControl.setValue(this.currentAmount)
      this.helpText = `Setup Auto Bid up to a maximum amount.  Next Max Bid `;
    } else {
      this.currentAmount = this.getNextBidAmount(bid, false);
      this.currentAmountControl.setValue(this.currentAmount)
      this.helpText = `Next Bid `;
    }
  }

  getNextBidAmount(bid, maxBid) {
    let nextAmount;
    let nextMultiplier = this.selectedBidType ? 2 : 1;

    let currentCalculatedAmount;
    const currentMultiplier = this.selectedBidType ? 1 : 2;

    this.currentAmount =  this.currentAmountControl.value;

    if (bid.currentItemBid <= 0) {
      nextMultiplier = 0;
    }

    if (maxBid) {
      nextMultiplier = 2;
    }

    if (bid.currentItemBid > 0) {
      nextAmount = bid.currentItemBid + nextMultiplier * bid.optionInfo.bidIncrement;
      currentCalculatedAmount = bid.currentItemBid + currentMultiplier * bid.optionInfo.bidIncrement;
    } else {
      nextAmount =  bid.optionInfo.minBid + nextMultiplier * bid.optionInfo.bidIncrement;
      currentCalculatedAmount = bid.optionInfo.minBid + currentMultiplier * bid.optionInfo.bidIncrement;
    }

    this.calculatedNextBidAmount = nextAmount;

    if (this.currentAmount !== currentCalculatedAmount || this.currentAmountControl.touched) { // value was changed by user
      if (this.currentAmount >= nextAmount) {
        return this.currentAmount;
      } else {
        this.currentAmountControl.markAsUntouched();
        return nextAmount;
      }
    } else {
      this.currentAmountControl.markAsUntouched();
      return nextAmount;
    }
  }

  onDismiss() {
    this.selectedBidId = null;
    this.selectedBidType = false;
    this.currentAmount = 0;
  }

  getAuctionStatus(campaign) {
    const result = {
      status: '',
      startedIn: '',
      finishedIn: ''
    };

    const AUCTION_CONTROL_MANUAL = 0;

    if (this.campaign.campaignData.auctionControlMethod === AUCTION_CONTROL_MANUAL) {
      switch (this.campaign.status) {
        case 0:
          result.status = 'Started'
          break;

        case 1:
          result.status = 'Bidding not available'
          break;
        case 2:
          result.status = 'Done'
          break;
      }
    } else {

    }

    return result;
  }
}
